import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';
import { deleteUser, getAvailableUserProducts, listUsers } from '../../services/usuarios';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import {
  getOwnerAvailableProducts,
  getUserProductsByKeys,
  isEnabledProduct,
} from './userProducts';
import {
  ACCOUNT_CARD_SX,
  ACCOUNT_PAGE_BG,
  AccountHomeButton,
  AccountSettingsButton,
} from '../common/AccountPageNavigation';

const MAX_USERS = 10;
const OWNER_ACCOUNT_COUNT = 1;

const CARD_SX = {
  ...ACCOUNT_CARD_SX,
};

const PRIMARY_BUTTON_SX = {
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.95rem',
  px: 3,
  py: 1.25,
  background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  boxShadow: '0 10px 24px -8px rgba(8, 145, 178, 0.45)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0e7490 0%, #155e75 100%)',
    boxShadow: '0 14px 32px -8px rgba(8, 145, 178, 0.6)',
  },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
};

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [availableProductKeys, setAvailableProductKeys] = useState(null);
  const {
    isAuthenticated,
    isHydrating,
    isOwner,
    atendabotAtendente,
    hasProductAccess,
    session,
  } = useAuthSession();
  const isAtendabotManager =
    atendabotAtendente !== true
    && typeof hasProductAccess === 'function'
    && hasProductAccess('atendabot');
  const canManageUsers = isOwner || isAtendabotManager;

  const allowedProducts = useMemo(
    () => (
      Array.isArray(availableProductKeys)
        ? getUserProductsByKeys(availableProductKeys)
        : getOwnerAvailableProducts(session)
    ),
    [availableProductKeys, session]
  );
  const restaurantAccountCount = users.length + OWNER_ACCOUNT_COUNT;
  const reachedUserLimit = restaurantAccountCount >= MAX_USERS;

  useEffect(() => {
    if (isHydrating) return;

    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    if (!canManageUsers) {
      toast.error('Você não tem permissão para acessar usuários.');
      navigate('/products', { replace: true });
    }
  }, [canManageUsers, isAuthenticated, isHydrating, navigate]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, productKeys] = await Promise.all([
        listUsers(),
        getAvailableUserProducts(),
      ]);
      setUsers(Array.isArray(data) ? data : []);
      setAvailableProductKeys(productKeys);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      const message = err?.response?.data?.error || err?.message;
      setError(message || 'Não foi possível carregar os usuários.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isHydrating || !canManageUsers) return;
    loadUsers();
  }, [canManageUsers, isHydrating, loadUsers]);

  const handleDelete = async () => {
    try {
      await deleteUser(deleteId);
      toast.success('Usuário removido com sucesso.');
      setDeleteId(null);
      loadUsers();
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
      const message = err?.response?.data?.error || err?.message;
      toast.error(message || 'Erro ao remover usuário.');
    }
  };

  const renderAccessChips = useCallback(
    (user) => {
      if (allowedProducts.length === 0) {
        return <Chip label="Nenhum acesso disponível" size="small" />;
      }

      const enabled = allowedProducts.filter((product) => isEnabledProduct(user[product.key]));

      if (enabled.length === 0) {
        return <Chip label="Sem acessos" size="small" variant="outlined" />;
      }

      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {enabled.map((product) => (
            <Chip
              key={product.key}
              label={
                product.key === 'atendabot'
                    ? `${product.label} (${user.atendabot_atendente ? 'Atendente' : 'Gerente'})`
                    : product.label
              }
              size="small"
              sx={{
                bgcolor: product.color,
                color: product.textColor,
                fontWeight: 600,
                mb: 0.5,
              }}
            />
          ))}
        </Stack>
      );
    },
    [allowedProducts]
  );

  if (isHydrating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: ACCOUNT_PAGE_BG }}>
        <CircularProgress sx={{ color: '#0891b2' }} />
      </Box>
    );
  }

  if (!canManageUsers) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', background: ACCOUNT_PAGE_BG, py: { xs: 4, md: 6 } }}>
      <AccountSettingsButton />
      <Box sx={{ maxWidth: 1150, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <AccountHomeButton />

          <Card sx={{ ...CARD_SX, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: '0 0 auto 0',
                height: 4,
                background: 'linear-gradient(90deg, #06b6d4 0%, #8b5cf6 50%, #f59e0b 100%)',
              }}
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, pt: { xs: 3, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      color: '#0891b2',
                      background: 'linear-gradient(135deg, rgba(8,145,178,.14), rgba(124,58,237,.10))',
                      border: '1px solid rgba(8,145,178,.18)',
                    }}
                  >
                    <PeopleAltOutlinedIcon sx={{ fontSize: 27 }} />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                      <Typography component="h1" variant="h5" fontWeight={700} sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
                        Usuários
                      </Typography>
                      {!loading && (
                        <Chip
                          size="small"
                          label={`${restaurantAccountCount} de ${MAX_USERS} contas`}
                          sx={{
                            fontWeight: 700,
                            color: reachedUserLimit ? '#92400e' : '#475569',
                            backgroundColor: reachedUserLimit ? '#fef3c7' : '#f1f5f9',
                          }}
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.4 }}>
                      Crie logins e gerencie os acessos vinculados ao seu restaurante.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/usuarios/novo')}
                  disabled={loading || reachedUserLimit}
                  sx={{ ...PRIMARY_BUTTON_SX, alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  Novo usuário
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {!loading && reachedUserLimit && (
            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
              O restaurante atingiu o limite de {MAX_USERS} contas. Exclua uma conta para criar outra.
            </Alert>
          )}

          <Card sx={CARD_SX}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Stack spacing={1.5}>
                  {[0, 1, 2].map((item) => (
                    <Skeleton key={item} variant="rectangular" height={56} sx={{ borderRadius: '12px' }} />
                  ))}
                </Stack>
              ) : users.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <PeopleAltOutlinedIcon sx={{ fontSize: 42, color: '#94a3b8', mb: 1 }} />
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#0f172a' }}>
                    Nenhum usuário cadastrado
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                    Crie o primeiro acesso para sua equipe.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow
                        sx={{
                          '& .MuiTableCell-root': {
                            py: 1.5,
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: '#475569',
                            backgroundColor: 'rgba(15, 23, 42, 0.025)',
                            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                          },
                        }}
                      >
                        <TableCell>Usuário</TableCell>
                        <TableCell>Acessos</TableCell>
                        {canManageUsers && <TableCell align="right">Ações</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody
                      sx={{
                        '& .MuiTableRow-root': { transition: 'background 0.15s ease' },
                        '& .MuiTableRow-root:hover': { backgroundColor: 'rgba(8, 145, 178, 0.04)' },
                        '& .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 'none' },
                        '& .MuiTableCell-root': {
                          py: 1.75,
                          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                        },
                      }}
                    >
                      {users.map((user) => (
                        <TableRow hover key={user.id}>
                          <TableCell>
                            <Typography fontWeight={600} sx={{ color: '#0f172a' }}>{user.username}</Typography>
                          </TableCell>
                          <TableCell>{renderAccessChips(user)}</TableCell>
                          {canManageUsers && (
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                <Tooltip title="Editar" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/usuarios/${user.id}`)}
                                    aria-label={`Editar usuário ${user.username}`}
                                    sx={{ color: '#0891b2', backgroundColor: 'rgba(8,145,178,.06)', borderRadius: '10px', '&:hover': { backgroundColor: 'rgba(8,145,178,.14)' } }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isOwner && (
                                  <Tooltip title="Excluir" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => setDeleteId(user.id)}
                                      aria-label={`Excluir usuário ${user.username}`}
                                      sx={{ color: '#dc2626', backgroundColor: 'rgba(220,38,38,.06)', borderRadius: '10px', '&:hover': { backgroundColor: 'rgba(220,38,38,.14)' } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>

        <ConfirmDialog
          open={Boolean(deleteId)}
          title="Excluir usuário"
          message="Deseja realmente excluir este usuário?"
          confirmText="Excluir"
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
        />
      </Box>
    </Box>
  );
};

export default UsersPage;
